// TradesView — the Builds board's third view (Oct 28, 2026; Greg: "I don't know if dispatch board is best
// option. Maybe the new builds board"). The queue Robert and Alex work trades from: Needs attention (the roof
// moved, no day yet, the day passed, waiting on a price, waiting on the homeowner's add-on signature), this week,
// next week, later, and what was done or cancelled in the last two weeks. "+ Add trade" finds the job first.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { addDaysISO, etDate, mondayOf } from "../../lib/weeklySpiffs";
import { searchDeals, type DealSearchHit } from "../../lib/opsRequests";
import {
  canMarkTradeDone, canTrade, fetchTradePrices, fetchTradeQueue, isOpenTrade, priceFor, tradeAttention, type Trade, type TradePrice,
} from "../../lib/trades";
import TradeCard from "./TradeCard";
import { useStaffNames, useTradeModals } from "./useTradeModals";

export default function TradesView() {
  const { role, isSuperUser } = useAuth();
  const canAct = canTrade(role, isSuperUser);
  const canDone = canMarkTradeDone(role, isSuperUser);
  const today = useMemo(() => etDate(new Date()), []);
  const [trades, setTrades] = useState<Trade[] | null>(null);
  const [prices, setPrices] = useState<Record<string, TradePrice>>({});
  const [err, setErr] = useState<string | null>(null);
  const names = useStaffNames();

  const load = useCallback(async () => {
    try {
      const rows = await fetchTradeQueue(today);
      setTrades(rows);
      setPrices(await fetchTradePrices(rows.map((t) => t.id)).catch(() => ({})));
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load the trades.");
    }
  }, [today]);
  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("trade_items", () => { void load(); });
  const modals = useTradeModals(today, () => void load());

  const buckets = useMemo(() => {
    const thisMon = mondayOf();
    const nextMon = addDaysISO(thisMon, 7);
    const after = addDaysISO(thisMon, 14);
    const out = { attention: [] as Trade[], week: [] as Trade[], next: [] as Trade[], later: [] as Trade[], done: [] as Trade[] };
    for (const t of trades ?? []) {
      if (!isOpenTrade(t)) { out.done.push(t); continue; }
      const p = priceFor(t, trades ?? [], prices);
      // A job waiting on the homeowner's add-on signature (Oct 29) is here too, on both visits of a pair.
      if (tradeAttention(t, today, p?.own ? p.price : null, p?.price ?? null)) out.attention.push(t);
      else if ((t.scheduled_date ?? "") < nextMon) out.week.push(t);
      else if ((t.scheduled_date ?? "") < after) out.next.push(t);
      else out.later.push(t);
    }
    const byDay = (a: Trade, b: Trade) => (a.scheduled_date ?? "9999").localeCompare(b.scheduled_date ?? "9999") || a.created_at.localeCompare(b.created_at);
    out.attention.sort(byDay); out.week.sort(byDay); out.next.sort(byDay); out.later.sort(byDay);
    out.done.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return out;
  }, [trades, prices, today]);

  const all = trades ?? [];
  const card = (t: Trade) => (
    <TradeCard
      key={t.id}
      t={t}
      partner={t.pair_id ? all.find((o) => o.pair_id === t.pair_id && o.id !== t.id) ?? null : null}
      price={priceFor(t, all, prices)}
      today={today}
      names={names}
      showJob
      canAct={canAct}
      canDone={canDone}
      onEdit={(x) => modals.openEdit(x, priceFor(x, all, prices)?.price ?? null)}
      onAction={modals.openAction}
      onChanged={() => void load()}
    />
  );

  const sections: Array<{ key: keyof typeof buckets; title: string; tone?: string; empty?: string }> = [
    { key: "attention", title: "Needs attention", tone: "text-amber-700", empty: "Nothing needs anyone right now." },
    { key: "week", title: "This week" },
    { key: "next", title: "Next week" },
    { key: "later", title: "Later" },
    { key: "done", title: "Done or cancelled, last two weeks" },
  ];

  return (
    <section>
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <p className="text-[12px] text-slate-500 min-w-0 flex-1 basis-56">
          Each visit goes on the Other Trades calendar with Robert, Alex, Jennifer, the sub and the job's RM invited.
        </p>
        {canAct && <AddTradeButton onPick={(id) => modals.openNew(id)} />}
      </div>
      {err && <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{err}</div>}
      {!trades && !err && <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />}
      {trades && trades.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 px-3 py-6 text-sm text-slate-400 text-center">
          No trades yet. {canAct ? "Add one here or from the deal page, or with + Trade on a build." : ""}
        </div>
      )}
      {trades && trades.length > 0 && sections.map((s) => {
        const list = buckets[s.key];
        if (!list.length && s.key !== "attention") return null;
        return (
          <div key={s.key} className="mb-5">
            <h2 className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${s.tone ?? "text-slate-500"}`}>{s.title} · {list.length}</h2>
            {list.length ? <div className="space-y-2">{list.map(card)}</div> : <p className="text-xs text-slate-400">{s.empty}</p>}
          </div>
        );
      })}
      {modals.element}
    </section>
  );
}

/** "+ Add trade" on the board: find the job, then the sheet opens on it. */
function AddTradeButton({ onPick }: { onPick: (dealId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<DealSearchHit[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setHits([]); return; }
    let alive = true;
    setBusy(true);
    const tm = setTimeout(() => {
      searchDeals(q).then((h) => { if (alive) setHits(h); }).catch(() => { if (alive) setHits([]); }).finally(() => { if (alive) setBusy(false); });
    }, 250);
    return () => { alive = false; clearTimeout(tm); };
  }, [q, open]);

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="ml-auto shrink-0 px-3 py-1.5 rounded-lg bg-bardin-orange text-white text-xs font-semibold shadow-sm">
        + Add trade
      </button>
    );
  }
  return (
    <div className="w-full sm:w-64 sm:ml-auto shrink-0">
      <div className="flex items-center gap-1">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Homeowner or street" aria-label="Find the job"
               className="min-w-0 flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm" />
        <button type="button" onClick={() => { setOpen(false); setQ(""); }} aria-label="Close" className="text-slate-400 px-1">×</button>
      </div>
      {(hits.length > 0 || busy) && (
        <div className="mt-1 rounded-lg border border-slate-200 bg-white shadow-sm max-h-60 overflow-y-auto">
          {busy && hits.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">Searching…</div>}
          {hits.map((h) => (
            <button key={h.id} type="button" onClick={() => { onPick(h.id); setOpen(false); setQ(""); }}
                    className="block w-full text-left px-2 py-1.5 text-[13px] hover:bg-slate-50">
              <b className="text-bardin-ink">{h.homeowner_name ?? "(no name)"}</b>
              <span className="text-slate-500"> · {[h.property_street, h.property_city].filter(Boolean).join(", ")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
